import React, {useCallback, useEffect, useRef, useState} from "react";
import {Link, useNavigate, useSearchParams} from "react-router-dom";
import {isAxiosError} from "axios";
import {ButtonV2} from "@/components/ui/ButtonsV2/ButtonV2";
import {CountrySelectorSimple} from "@/components/paginated-selector/country-selector-simple";
import {FacilitySelectV2} from "@/lib/facilities/FacilitySelectV2";
import {ClientSelectV2} from "@/lib/clients/ClientSelectV2";
import {useAuth} from "@/context/user";
import {Client, Department} from "@/types/userManagement";
import {DepartmentSelectV2} from "@/lib/departments/DepartmentSelectV2";
import {IncidentFeedSelectorSimple} from "@/components/paginated-selector/incident-feed-selector-simple";
import {useIncidentFeeds} from "@/hooks/useIncidentFeeds";
import {TagSelector} from "@/components/incident-tags/TagSelector";
import {UserSelectorSimple} from "@/components/paginated-selector/user-selector-simple";
import {NotificationGroupSelectorSimple} from "@/components/paginated-selector/notification-group-selector-simple";
import {FacilitySelectorSimple} from "@/components/paginated-selector/facility-selector-simple";
import {CategorySelector} from "@/components/incident-categories/CategorySelector";
import {SeveritySelector} from "@/components/incident-severity/SeveritySelector";
import {apiV2} from "@/api/axiosConfig";
import {APIProvider, Map, AdvancedMarker, useMap} from "@vis.gl/react-google-maps";
import {Circle} from "@/features/operational-map-v2/circle";
import {countries} from "@/lib/countries";

type LocationType = "country" | "facility" | "custom" | null;

type Country = {
    countryCode: string;
    countryNameEn: string;
    flag: string;
};

type Facility = {
    id: string;
    name: string;
    clientID: string;
    clientName: string;
    address: {
        formatted: string;
    };
    position?: {
        type: string;
        coordinates: [number, number]; // [longitude, latitude]
    };
};

type Tag = [string, string]; // [slug, label]

type AlertFormData = {
    name?: string;
    locationType: LocationType;
    client?: Client;
    countryCodes?: string[];
    facility?: Facility; // Deprecated - kept for backward compatibility
    facilities?: Facility[];
    facilityIDs?: string[];
    radius?: number;
    customShapes?: Array<{
        type: "circle" | "polygon" | "polyline";
        center?: number[]; // [lng, lat] for circles
        radius?: number; // in meters
        geometry?: number[][]; // [[lng, lat], ...] for polygons/polylines
    }>;
    incidentType?: "tags" | "categories";
    incidentFeedIDs?: string[];
    incidentTags?: Tag[];
    excludedTags?: Tag[];
    tagMode?: "all" | "any";
    categoryTypeIDs?: string[];
    incidentSeverities?: string[];
    notifyFacilityGuardians?: boolean;
    notifyTrackingTeam?: boolean;
    alertRecipients?: string[];
    notificationGroupIDs?: string[];
    department?: Department;
    // Add more fields as you add steps
}

type StepProps = {
    data: AlertFormData;
    onUpdate: (updates: Partial<AlertFormData>) => void;
    onNext: () => void;
    onBack: () => void;
    isSuperAdmin?: boolean;
    isDepartmentAdmin?: boolean;
}

// Step 1: Location Type Selection
function LocationTypeStep({data, onUpdate, onNext, isSuperAdmin, isDepartmentAdmin}: StepProps) {
    const [feedType, setFeedType] = useState<string | null>(null);
    const {clientID: authClientID} = useAuth();
    const effectiveClientID = isSuperAdmin ? data.client?.id : authClientID;
    const {hasOnlyDefaultFeed, defaultFeedID} = useIncidentFeeds(effectiveClientID);

    // Auto-select the default feed when it's the only one available
    useEffect(() => {
        if (hasOnlyDefaultFeed && defaultFeedID && (!data.incidentFeedIDs || data.incidentFeedIDs.length === 0)) {
            onUpdate({incidentFeedIDs: [defaultFeedID]});
        }
    }, [hasOnlyDefaultFeed, defaultFeedID]);

    const handleSelect = useCallback((type: LocationType) => {
        // Clear country/facility/facilities/facilityIDs/radius/customShapes when switching location type
        onUpdate({locationType: type, countryCodes: undefined, facility: undefined, facilities: undefined, facilityIDs: undefined, radius: undefined, customShapes: undefined});
    }, [onUpdate]);

    const handleClientChange = useCallback((client: Client | undefined) => {
        // Clear incident feed IDs and department when client changes
        onUpdate({client, incidentFeedIDs: [], department: undefined});
    }, [onUpdate]);

    const handleDepartmentChange = useCallback((department: Department | undefined) => {
        onUpdate({department, facilityIDs: [], alertRecipients: [], notificationGroupIDs: []});
    }, [onUpdate]);

    const handleNameChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        onUpdate({name: e.target.value});
    }, [onUpdate]);

    const handleIncidentFeedIDsChange = useCallback((feedIDs: string[]) => {
        onUpdate({incidentFeedIDs: feedIDs});
    }, [onUpdate]);

    // Fetch feed type when feed IDs change
    useEffect(() => {
        if (!data.incidentFeedIDs || data.incidentFeedIDs.length === 0) {
            setFeedType(null);
            onUpdate({incidentType: undefined});
            return;
        }

        const fetchFeedType = async () => {
            // Double-check to satisfy TypeScript
            if (!data.incidentFeedIDs || data.incidentFeedIDs.length === 0) return;

            try {
                // Fetch the first selected feed to determine type
                const response = await apiV2.get(`incident-feeds/${data.incidentFeedIDs[0]}`);
                const feed = response.data;
                setFeedType(feed.type);

                // Automatically set incident type based on feed type
                if (feed.type === "intelligence_fusion") {
                    onUpdate({incidentType: "categories"});
                } else if (feed.type === "samdesk") {
                    onUpdate({incidentType: "tags"});
                }
            } catch (error) {
                console.error("Failed to fetch feed type:", error);
            }
        };

        fetchFeedType();
    }, [data.incidentFeedIDs, onUpdate]);

    const canProceed = data.name && data.name.trim() !== "" && data.locationType !== null && (!isSuperAdmin || data.client) && data.incidentFeedIDs && data.incidentFeedIDs.length > 0 && (!isDepartmentAdmin || data.department);

    return (
        <div className="flex flex-col gap-6">
            <h2 className="text-xl font-semibold">Select Location Type</h2>

            <div className="flex flex-col gap-2">
                <label className="font-medium">
                    Alert Name <span className="text-dark-red">*</span>
                </label>
                <input
                    type="text"
                    value={data.name || ""}
                    onChange={handleNameChange}
                    className="border border-light-grey rounded px-3 py-2"
                    placeholder="Enter alert name"
                    required
                />
            </div>

            {isSuperAdmin && (
                <ClientSelectV2
                    label="Client"
                    required
                    onChange={handleClientChange}
                    initialID={data.client?.id}
                />
            )}

            {(!isSuperAdmin || data.client) && (
                <div className="flex flex-col gap-1">
                    <label className="font-medium">
                        Department{isDepartmentAdmin && <span className="text-dark-red"> *</span>}
                    </label>
                    <DepartmentSelectV2
                        clientID={data.client?.id}
                        initialID={data.department?.id}
                        onChange={handleDepartmentChange}
                        isDisabled={isSuperAdmin && !data.client}
                    />
                </div>
            )}

            {(!isSuperAdmin || data.client) && !hasOnlyDefaultFeed && (
                <div className="flex flex-col gap-2">
                    <label className="font-medium">
                        Incident Feeds <span className="text-dark-red">*</span>
                    </label>
                    <p className="text-sm text-grey mb-2">
                        Select which incident feeds to monitor for this alert.
                    </p>
                    <IncidentFeedSelectorSimple
                        clientID={data.client?.id}
                        selectedIDs={data.incidentFeedIDs || []}
                        setSelectedIDs={handleIncidentFeedIDsChange}
                    />
                </div>
            )}

            <div className="grid grid-cols-3 gap-4">
                <button
                    onClick={() => handleSelect("country")}
                    className={`p-6 border-2 rounded-lg transition-all ${
                        data.locationType === "country"
                            ? "border-blue bg-light-blue"
                            : "border-light-light-grey hover:border-light-grey"
                    }`}
                >
                    <div className="text-lg font-semibold">Country</div>
                    <div className="text-sm text-grey mt-2">
                        Create alerts for an entire country
                    </div>
                </button>
                <button
                    onClick={() => handleSelect("facility")}
                    className={`p-6 border-2 rounded-lg transition-all ${
                        data.locationType === "facility"
                            ? "border-blue bg-light-blue"
                            : "border-light-light-grey hover:border-light-grey"
                    }`}
                >
                    <div className="text-lg font-semibold">Facility</div>
                    <div className="text-sm text-grey mt-2">
                        Create alerts for one or more facilities
                    </div>
                </button>
                <button
                    onClick={() => handleSelect("custom")}
                    className={`p-6 border-2 rounded-lg transition-all ${
                        data.locationType === "custom"
                            ? "border-blue bg-light-blue"
                            : "border-light-light-grey hover:border-light-grey"
                    }`}
                >
                    <div className="text-lg font-semibold">Custom</div>
                    <div className="text-sm text-grey mt-2">
                        Draw custom shapes on a map
                    </div>
                </button>
            </div>
            <div className="flex justify-end">
                <ButtonV2
                    label="Next"
                    onClick={onNext}
                    disabled={!canProceed}
                />
            </div>
        </div>
    );
}

// Step 2: Location Selection
function LocationSelectionStep({data, onUpdate, onNext, onBack}: StepProps) {
    const googleMapsApiKey: string = process.env.REACT_APP_GOOGLE_MAP_API_KEY!;
    const [facilities, setFacilities] = useState<Facility[]>([]);
    const [loadingFacilities, setLoadingFacilities] = useState(false);

    const handleCountryCodesChange = useCallback((countryCodes: string[]) => {
        onUpdate({countryCodes});
    }, [onUpdate]);

    const handleFacilityIDsChange = useCallback((facilityIDs: string[]) => {
        onUpdate({facilityIDs});
    }, [onUpdate]);

    // Fetch facility details when facility IDs change
    useEffect(() => {
        if (!data.facilityIDs || data.facilityIDs.length === 0) {
            setFacilities([]);
            onUpdate({facilities: []});
            return;
        }

        setLoadingFacilities(true);
        const fetchFacilities = async () => {
            try {
                const facilityPromises = data.facilityIDs!.map(id =>
                    apiV2.get<Facility>(`facilities/${id}`)
                );
                const responses = await Promise.all(facilityPromises);
                const fetchedFacilities = responses.map(res => res.data);
                setFacilities(fetchedFacilities);
                onUpdate({facilities: fetchedFacilities});
            } catch (error) {
                console.error("Failed to fetch facilities:", error);
            } finally {
                setLoadingFacilities(false);
            }
        };

        fetchFacilities();
    }, [data.facilityIDs]);

    const handleRadiusChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        const km = parseFloat(e.target.value);
        // Convert km to meters for storage
        const meters = isNaN(km) ? undefined : Math.round(km * 1000);
        onUpdate({radius: meters});
    }, [onUpdate]);

    const canProceed = (data.locationType === "country" && data.countryCodes && data.countryCodes.length > 0) ||
                       (data.locationType === "facility" && data.facilityIDs && data.facilityIDs.length > 0 && data.radius);

    // Get facility positions for map preview (GeoJSON format: [longitude, latitude])
    const facilityPositions = facilities
        .filter(f => f.position)
        .map(f => ({
            id: f.id,
            name: f.name,
            lat: f.position!.coordinates[1],
            lng: f.position!.coordinates[0]
        }));

    return (
        <div className="flex flex-col gap-6 overflow-visible">
            <h2 className="text-xl font-semibold">
                {data.locationType === "country" ? "Select Country" : "Select Facility"}
            </h2>

            {data.locationType === "country" && (
                <div className="flex flex-col gap-2">
                    <label className="font-medium">
                        Countries <span className="text-dark-red">*</span>
                    </label>
                    <CountrySelectorSimple
                        selectedIDs={data.countryCodes || []}
                        setSelectedIDs={handleCountryCodesChange}
                    />
                </div>
            )}

            {data.locationType === "facility" && (
                <>
                    <div className="flex flex-col gap-2">
                        <label className="font-medium">
                            Facilities <span className="text-dark-red">*</span>
                        </label>
                        <FacilitySelectorSimple
                            clientID={data.client?.id}
                            departmentID={data.department?.id}
                            selectedIDs={data.facilityIDs || []}
                            setSelectedIDs={handleFacilityIDsChange}
                        />
                    </div>
                    <div className="flex flex-col gap-2">
                        <label className="font-medium">
                            Radius (km) <span className="text-dark-red">*</span>
                        </label>
                        <input
                            type="number"
                            value={data.radius ? data.radius / 1000 : ""}
                            onChange={handleRadiusChange}
                            className="border border-light-grey rounded px-3 py-2"
                            placeholder="e.g. 5"
                            min="0"
                            step="0.1"
                            required
                        />
                    </div>

                    {/* Map Preview */}
                    {facilityPositions.length > 0 && (
                        <div className="flex flex-col gap-2">
                            <label className="font-medium">Map Preview</label>
                            <div className="w-full h-96 border border-light-grey rounded overflow-hidden">
                                <APIProvider apiKey={googleMapsApiKey}>
                                    <Map
                                        key={data.facilityIDs?.join(",")}
                                        mapId={process.env.REACT_APP_GOOGLE_MAP_ID}
                                        defaultCenter={facilityPositions[0]}
                                        defaultZoom={12}
                                        gestureHandling="greedy"
                                        disableDefaultUI={false}
                                        zoomControl={true}
                                        fullscreenControl={false}
                                        streetViewControl={false}
                                        mapTypeControl={true}
                                        style={{width: "100%", height: "100%"}}
                                    >
                                        {/* Facility Markers */}
                                        {facilityPositions.map((facility) => (
                                            <React.Fragment key={facility.id}>
                                                <AdvancedMarker position={{lat: facility.lat, lng: facility.lng}}>
                                                    <div
                                                        className="w-8 h-8 bg-blue-500 rounded-full border-4 border-white shadow-lg flex items-center justify-center"
                                                        style={{ transform: "translate(0, 50%)" }}
                                                        title={facility.name}
                                                    >
                                                        <div className="w-2 h-2 bg-white rounded-full"></div>
                                                    </div>
                                                </AdvancedMarker>

                                                {/* Radius Circle for each facility */}
                                                {data.radius && (
                                                    <Circle
                                                        center={{lat: facility.lat, lng: facility.lng}}
                                                        radius={data.radius} // radius is in meters
                                                        strokeColor="#2563eb"
                                                        strokeOpacity={0.8}
                                                        strokeWeight={2}
                                                        fillColor="#3b82f6"
                                                        fillOpacity={0.2}
                                                    />
                                                )}
                                            </React.Fragment>
                                        ))}
                                    </Map>
                                </APIProvider>
                            </div>
                            <p className="text-sm text-grey">
                                {facilityPositions.length === 1
                                    ? "The blue circle shows the alert radius around the facility"
                                    : `The blue circles show the alert radius around the ${facilityPositions.length} facilities`}
                            </p>
                        </div>
                    )}
                </>
            )}

            <div className="flex justify-between gap-8">
                <ButtonV2
                    label="Back"
                    onClick={onBack}
                    color="gray"
                />
                <ButtonV2
                    label="Next"
                    onClick={onNext}
                    disabled={!canProceed}
                />
            </div>
        </div>
    );
}

// Helper component to initialize drawing manager - must be inside Map component
function DrawingManagerInitializer({
    onShapesChange,
    initialShapes
}: {
    onShapesChange: (shapes: google.maps.MVCObject[]) => void;
    initialShapes?: AlertFormData["customShapes"];
}) {
    const map = useMap();
    const onShapesChangeRef = useRef(onShapesChange);
    useEffect(() => { onShapesChangeRef.current = onShapesChange; }, [onShapesChange]);
    const [shapes, setShapes] = useState<google.maps.MVCObject[]>([]);
    const shapesRef = useRef<google.maps.MVCObject[]>([]);
    // Tracks whether onShapesChange has been called with the initial shapes already,
    // so StrictMode's remount can recreate overlays without calling onShapesChange again.
    const initialShapesNotifiedRef = useRef(false);


    // Cleanup shapes and drawing manager on unmount (handles StrictMode double-invoke)
    const drawingManagerRef = useRef<google.maps.drawing.DrawingManager | null>(null);
    useEffect(() => {
        return () => {
            shapesRef.current.forEach(shape => {
                if (shape instanceof google.maps.Circle || shape instanceof google.maps.Polygon || shape instanceof google.maps.Polyline) {
                    shape.setMap(null);
                }
            });
            if (drawingManagerRef.current) {
                drawingManagerRef.current.setMap(null);
                drawingManagerRef.current = null;
            }
        };
    }, []);

    // Initialize drawing manager and load initial shapes when map is available
    useEffect(() => {
        if (!map) return;

        const manager = new google.maps.drawing.DrawingManager({
            drawingMode: null,
            drawingControl: true,
            drawingControlOptions: {
                position: google.maps.ControlPosition.TOP_CENTER,
                drawingModes: [
                    google.maps.drawing.OverlayType.CIRCLE,
                    google.maps.drawing.OverlayType.POLYGON,
                    google.maps.drawing.OverlayType.POLYLINE,
                ],
            },
            circleOptions: {
                fillColor: "#3b82f6",
                fillOpacity: 0.2,
                strokeWeight: 2,
                strokeColor: "#2563eb",
                clickable: true,
                editable: true,
                draggable: true,
                zIndex: 1,
            },
            polygonOptions: {
                fillColor: "#3b82f6",
                fillOpacity: 0.2,
                strokeWeight: 2,
                strokeColor: "#2563eb",
                clickable: true,
                editable: true,
                draggable: true,
                zIndex: 1,
            },
            polylineOptions: {
                strokeWeight: 3,
                strokeColor: "#2563eb",
                clickable: true,
                editable: true,
                draggable: true,
                zIndex: 1,
            },
        });

        manager.setMap(map);
        drawingManagerRef.current = manager;

        // Listen for shape completion
        google.maps.event.addListener(manager, "overlaycomplete", (event: google.maps.drawing.OverlayCompleteEvent) => {
            // Prevent Google Maps from auto-zooming after shape is drawn
            const savedZoom = map.getZoom();
            const savedCenter = map.getCenter();
            const lockZoom = google.maps.event.addListener(map, "zoom_changed", () => {
                google.maps.event.removeListener(lockZoom);
                if (savedZoom !== undefined) map.setZoom(savedZoom);
                if (savedCenter) map.setCenter(savedCenter);
            });

            if (event.overlay) {
                google.maps.event.addListener(event.overlay, "rightclick", () => {
                    event.overlay?.setMap(null);
                    setShapes(prev => {
                        const newShapes = prev.filter(s => s !== event.overlay);
                        shapesRef.current = newShapes;
                        onShapesChangeRef.current(newShapes);
                        return newShapes;
                    });
                });

                setShapes(prev => {
                    const newShapes = [...prev, event.overlay!];
                    shapesRef.current = newShapes;
                    onShapesChangeRef.current(newShapes);
                    return newShapes;
                });
            }
            manager.setDrawingMode(null);
        });

        // Load initial shapes onto the map (always, so state is correct after StrictMode remount)
        if (initialShapes && initialShapes.length > 0) {
            const loadedShapes: google.maps.MVCObject[] = [];

            initialShapes.forEach((shapeData) => {
                if (shapeData.type === "circle" && shapeData.center && shapeData.radius) {
                    const center = { lat: shapeData.center[1], lng: shapeData.center[0] };
                    const circle = new google.maps.Circle({
                        center, radius: shapeData.radius,
                        fillColor: "#3b82f6", fillOpacity: 0.2,
                        strokeWeight: 2, strokeColor: "#2563eb",
                        clickable: true, editable: true, draggable: true, zIndex: 1, map,
                    });
                    google.maps.event.addListener(circle, "rightclick", () => {
                        circle.setMap(null);
                        setShapes(prev => { const s = prev.filter(x => x !== circle); shapesRef.current = s; onShapesChangeRef.current(s); return s; });
                    });
                    loadedShapes.push(circle);
                } else if (shapeData.type === "polygon" && shapeData.geometry) {
                    const paths = shapeData.geometry.map(c => ({ lat: c[1], lng: c[0] }));
                    const polygon = new google.maps.Polygon({
                        paths,
                        fillColor: "#3b82f6", fillOpacity: 0.2,
                        strokeWeight: 2, strokeColor: "#2563eb",
                        clickable: true, editable: true, draggable: true, zIndex: 1, map,
                    });
                    google.maps.event.addListener(polygon, "rightclick", () => {
                        polygon.setMap(null);
                        setShapes(prev => { const s = prev.filter(x => x !== polygon); shapesRef.current = s; onShapesChangeRef.current(s); return s; });
                    });
                    loadedShapes.push(polygon);
                } else if (shapeData.type === "polyline" && shapeData.geometry) {
                    const path = shapeData.geometry.map(c => ({ lat: c[1], lng: c[0] }));
                    const polyline = new google.maps.Polyline({
                        path,
                        strokeWeight: 3, strokeColor: "#2563eb",
                        clickable: true, editable: true, draggable: true, zIndex: 1, map,
                    });
                    google.maps.event.addListener(polyline, "rightclick", () => {
                        polyline.setMap(null);
                        setShapes(prev => { const s = prev.filter(x => x !== polyline); shapesRef.current = s; onShapesChangeRef.current(s); return s; });
                    });
                    loadedShapes.push(polyline);
                }
            });

            if (loadedShapes.length > 0) {
                setShapes(loadedShapes);
                shapesRef.current = loadedShapes;
                // Only notify parent once — not on StrictMode's second mount
                if (!initialShapesNotifiedRef.current) {
                    initialShapesNotifiedRef.current = true;
                    onShapesChangeRef.current(loadedShapes);
                }

                // Fit map to shapes
                const bounds = new google.maps.LatLngBounds();
                loadedShapes.forEach(shape => {
                    if (shape instanceof google.maps.Circle) {
                        const center = shape.getCenter();
                        if (center) bounds.extend(center);
                    } else if (shape instanceof google.maps.Polygon) {
                        shape.getPath().forEach(ll => bounds.extend(ll));
                    } else if (shape instanceof google.maps.Polyline) {
                        shape.getPath().forEach(ll => bounds.extend(ll));
                    }
                });
                if (!bounds.isEmpty()) {
                    map.fitBounds(bounds, 100);
                    google.maps.event.addListenerOnce(map, "bounds_changed", () => {
                        const z = map.getZoom();
                        if (z && z > 12) map.setZoom(12);
                    });
                }
            }
        }
    }, [map]);

    return null;
}

// Step 2b: Custom Location Drawing
function CustomLocationStep({data, onUpdate, onNext, onBack}: StepProps) {
    const googleMapsApiKey: string = process.env.REACT_APP_GOOGLE_MAP_API_KEY!;
    const [shapes, setShapes] = useState<google.maps.MVCObject[]>([]);
    const initialShapesRef = useRef(data.customShapes);

    // Handle shapes change from drawing manager
    const handleShapesChange = useCallback((newShapes: google.maps.MVCObject[]) => {
        setShapes(newShapes);

        const shapesData = newShapes.map(shape => {
            if (shape instanceof google.maps.Circle) {
                const center = shape.getCenter();
                if (!center) return null;
                return {
                    type: "circle" as const,
                    center: [center.lng(), center.lat()], // [lng, lat] format
                    radius: shape.getRadius(),
                };
            } else if (shape instanceof google.maps.Polygon) {
                const path = shape.getPath();
                const geometry = path.getArray().map(latLng => [latLng.lng(), latLng.lat()]); // [[lng, lat], ...]
                return {
                    type: "polygon" as const,
                    geometry,
                };
            } else if (shape instanceof google.maps.Polyline) {
                const path = shape.getPath();
                const geometry = path.getArray().map(latLng => [latLng.lng(), latLng.lat()]); // [[lng, lat], ...]
                return {
                    type: "polyline" as const,
                    geometry,
                };
            }
            return null;
        }).filter(Boolean) as AlertFormData["customShapes"];

        onUpdate({ customShapes: shapesData });
    }, [onUpdate]);

    // Clear all shapes
    const handleClearAll = useCallback(() => {
        shapes.forEach(shape => {
            if (shape instanceof google.maps.Circle || shape instanceof google.maps.Polygon || shape instanceof google.maps.Polyline) {
                shape.setMap(null);
            }
        });
        handleShapesChange([]);
    }, [shapes, handleShapesChange]);

    const canProceed = shapes.length > 0;

    return (
        <div className="flex flex-col gap-6">
            <h2 className="text-xl font-semibold">Draw Custom Alert Zones</h2>

            <div className="bg-light-blue border border-blue rounded p-4">
                <div className="font-medium mb-2">Instructions:</div>
                <ul className="text-sm text-grey space-y-1 list-disc list-inside">
                    <li>Use the drawing tools at the top of the map to draw circles, polygons, or polylines</li>
                    <li>You can draw multiple shapes to create complex alert zones</li>
                    <li>Click and drag shapes to move them</li>
                    <li>Click the edges of shapes to edit their size/shape</li>
                    <li>Right-click on a shape to delete it</li>
                </ul>
            </div>

            {/* Map */}
            <div className="flex flex-col gap-2">
                <label className="font-medium">Map</label>
                <div className="w-full h-[600px] border border-light-grey rounded overflow-hidden">
                    <APIProvider apiKey={googleMapsApiKey} libraries={["drawing", "places"]}>
                        <Map
                            mapId={process.env.REACT_APP_GOOGLE_MAP_ID}
                            defaultCenter={{ lat: 20, lng: 0 }}
                            defaultZoom={2}
                            gestureHandling="greedy"
                            disableDefaultUI={false}
                            zoomControl={true}
                            fullscreenControl={false}
                            streetViewControl={false}
                            mapTypeControl={true}
                            style={{width: "100%", height: "100%"}}
                        >
                            <DrawingManagerInitializer
                                onShapesChange={handleShapesChange}
                                initialShapes={initialShapesRef.current}
                            />
                        </Map>
                    </APIProvider>
                </div>
                <p className="text-sm text-grey">
                    {shapes.length === 0
                        ? "No shapes drawn yet. Use the drawing tools at the top of the map to get started."
                        : `${shapes.length} shape(s) drawn. Incidents within these areas will trigger alerts.`
                    }
                </p>
            </div>

            {/* Clear button */}
            {shapes.length > 0 && (
                <div>
                    <ButtonV2
                        label="Clear All Shapes"
                        onClick={handleClearAll}
                        color="gray"
                    />
                </div>
            )}

            <div className="flex justify-between gap-8">
                <ButtonV2
                    label="Back"
                    onClick={onBack}
                    color="gray"
                />
                <ButtonV2
                    label="Next"
                    onClick={onNext}
                    disabled={!canProceed}
                />
            </div>
        </div>
    );
}

// Step 3: Incident Mode Type Selection
function IncidentModeTypeStep({data, onUpdate, onNext, onBack}: StepProps) {
    const handleSelect = useCallback((type: "tags" | "categories") => {
        onUpdate({incidentType: type});
    }, [onUpdate]);

    const canProceed = data.incidentType !== undefined;

    return (
        <div className="flex flex-col gap-6">
            <h2 className="text-xl font-semibold">Select Incident Mode Type</h2>
            <div className="flex gap-4">
                <button
                    onClick={() => handleSelect("tags")}
                    className={`flex-1 p-6 border-2 rounded-lg transition-all ${
                        data.incidentType === "tags"
                            ? "border-blue bg-light-blue"
                            : "border-light-light-grey hover:border-light-grey"
                    }`}
                >
                    <div className="text-lg font-semibold">Tags</div>
                    <div className="text-sm text-grey mt-2">
                        Filter incidents by tags
                    </div>
                </button>
                <button
                    onClick={() => handleSelect("categories")}
                    className={`flex-1 p-6 border-2 rounded-lg transition-all ${
                        data.incidentType === "categories"
                            ? "border-blue bg-light-blue"
                            : "border-light-light-grey hover:border-light-grey"
                    }`}
                >
                    <div className="text-lg font-semibold">Categories</div>
                    <div className="text-sm text-grey mt-2">
                        Filter incidents by categories
                    </div>
                </button>
            </div>
            <div className="flex justify-between gap-8">
                <ButtonV2
                    label="Back"
                    onClick={onBack}
                    color="gray"
                />
                <ButtonV2
                    label="Next"
                    onClick={onNext}
                    disabled={!canProceed}
                />
            </div>
        </div>
    );
}

// Step 4a: Category Selection (only shown if incident type is "categories")
function CategorySelectionStep({data, onUpdate, onNext, onBack}: StepProps) {
    const handleCategoryTypeIDsChange = useCallback((typeIDs: string[]) => {
        onUpdate({categoryTypeIDs: typeIDs});
    }, [onUpdate]);

    const handleIncidentSeveritiesChange = useCallback((severities: string[]) => {
        onUpdate({incidentSeverities: severities});
    }, [onUpdate]);

    const canProceed = true; // Allow proceeding with no selections (means "all")

    return (
        <div className="flex flex-col gap-6">
            <h2 className="text-xl font-semibold">Select Incident Categories and Severities</h2>

            <div className="bg-light-blue border border-blue rounded p-3 mb-2">
                <p className="text-sm">
                    <strong>Note:</strong> Selecting no categories or severities will match all incidents. Both category and severity must match for an alert to trigger.
                </p>
            </div>

            <div className="flex flex-col gap-2">
                <label className="font-medium">Categories and Types</label>
                <p className="text-sm text-grey mb-2">
                    Select individual types or entire categories. Leave empty to match all categories.
                </p>
                <CategorySelector
                    selectedTypeIDs={data.categoryTypeIDs || []}
                    onSelectedTypeIDsChange={handleCategoryTypeIDsChange}
                />
            </div>

            <div className="flex flex-col gap-2">
                <SeveritySelector
                    selectedSeverities={data.incidentSeverities || []}
                    onSelectedSeveritiesChange={handleIncidentSeveritiesChange}
                />
                <p className="text-sm text-grey mt-1">
                    Leave empty to match all severities.
                </p>
            </div>

            <div className="flex justify-between gap-8">
                <ButtonV2
                    label="Back"
                    onClick={onBack}
                    color="gray"
                />
                <ButtonV2
                    label="Next"
                    onClick={onNext}
                    disabled={!canProceed}
                />
            </div>
        </div>
    );
}

// Step 4b: Tag Selection (only shown if incident type is "tags")
function TagSelectionStep({data, onUpdate, onNext, onBack}: StepProps) {
    const handleIncidentTagsChange = useCallback((tags: Tag[]) => {
        onUpdate({incidentTags: tags});
    }, [onUpdate]);

    const handleExcludedTagsChange = useCallback((tags: Tag[]) => {
        onUpdate({excludedTags: tags});
    }, [onUpdate]);

    const handleTagModeChange = useCallback((mode: "all" | "any") => {
        onUpdate({tagMode: mode});
    }, [onUpdate]);

    const canProceed = true; // Allow proceeding with no tags (means "all incidents from feeds")

    return (
        <div className="flex flex-col gap-6">
            <h2 className="text-xl font-semibold">Select Tags</h2>

            <div className="bg-light-blue border border-blue rounded p-3 mb-2">
                <p className="text-sm">
                    <strong>Note:</strong> Selecting no tags will match all incidents from the selected feeds (except those with excluded tags).
                </p>
            </div>

            <div className="flex flex-col gap-2">
                <label className="font-medium">Tags</label>
                <p className="text-sm text-grey mb-2">
                    Leave included tags empty to match all incidents (except those with excluded tags).
                </p>
                <TagSelector
                    includedTags={data.incidentTags || []}
                    excludedTags={data.excludedTags || []}
                    tagMode={data.tagMode || "any"}
                    onIncludedTagsChange={handleIncidentTagsChange}
                    onExcludedTagsChange={handleExcludedTagsChange}
                    onTagModeChange={handleTagModeChange}
                />
            </div>

            <div className="flex justify-between gap-8">
                <ButtonV2
                    label="Back"
                    onClick={onBack}
                    color="gray"
                />
                <ButtonV2
                    label="Next"
                    onClick={onNext}
                    disabled={!canProceed}
                />
            </div>
        </div>
    );
}

// Notification Step: Facility Guardians and Alert Recipients
function NotificationStep({data, onUpdate, onNext, onBack, isDepartmentAdmin}: StepProps) {
    const handleNotifyGuardiansChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        onUpdate({notifyFacilityGuardians: e.target.checked});
    }, [onUpdate]);

    const handleNotifyTrackingTeamChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        onUpdate({notifyTrackingTeam: e.target.checked});
    }, [onUpdate]);

    const handleRecipientsChange = useCallback((recipients: string[]) => {
        onUpdate({alertRecipients: recipients});
    }, [onUpdate]);

    const handleGroupsChange = useCallback((groupIDs: string[]) => {
        onUpdate({notificationGroupIDs: groupIDs});
    }, [onUpdate]);

    return (
        <div className="flex flex-col gap-6">
            <h2 className="text-xl font-semibold">Notification Settings</h2>

            {/* Only show facility guardians option if location type is facility */}
            {data.locationType === "facility" && (
                <div className="border border-light-light-grey rounded p-4">
                    <label className="flex items-center gap-3 cursor-pointer">
                        <input
                            type="checkbox"
                            checked={data.notifyFacilityGuardians || false}
                            onChange={handleNotifyGuardiansChange}
                            className="w-4 h-4 accent-blue"
                        />
                        <span className="font-medium">Notify Facility Guardians</span>
                    </label>
                    <p className="text-sm text-grey mt-2 ml-7">
                        Send alerts to all guardians assigned to this facility
                    </p>
                </div>
            )}

            <div className="border border-light-light-grey rounded p-4">
                <label className="flex items-center gap-3 cursor-pointer">
                    <input
                        type="checkbox"
                        checked={data.notifyTrackingTeam || false}
                        onChange={handleNotifyTrackingTeamChange}
                        className="w-4 h-4 accent-blue"
                    />
                    <span className="font-medium">Notify Tracking Team</span>
                </label>
                <p className="text-sm text-grey mt-2 ml-7">
                    Send alerts to the tracking team when this alert triggers
                </p>
            </div>

            <div className="flex flex-col gap-2">
                <label className="font-medium">Alert Recipients</label>
                <p className="text-sm text-grey mb-2">
                    Select users who should receive these alerts
                </p>
                <UserSelectorSimple
                    clientID={data.client?.id}
                    departmentID={data.department?.id}
                    selectedIDs={data.alertRecipients || []}
                    setSelectedIDs={handleRecipientsChange}
                />
            </div>

            <div className="flex flex-col gap-2">
                <label className="font-medium">Notification Groups</label>
                <p className="text-sm text-grey mb-2">
                    Select notification groups whose members should receive these alerts
                </p>
                <NotificationGroupSelectorSimple
                    clientID={data.client?.id}
                    departmentID={data.department?.id}
                    selectedIDs={data.notificationGroupIDs || []}
                    setSelectedIDs={handleGroupsChange}
                />
            </div>

            <div className="flex justify-between gap-8">
                <ButtonV2
                    label="Back"
                    onClick={onBack}
                    color="gray"
                />
                <ButtonV2
                    label="Next"
                    onClick={onNext}
                />
            </div>
        </div>
    );
}

// Final Step: Review and Save
function ReviewStep({data, onBack, navigate, alertId, mode, prevUrl}: StepProps & {navigate: ReturnType<typeof useNavigate>, alertId?: string, mode?: "create" | "edit", prevUrl: string}) {
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");

    const handleSave = useCallback(async () => {
        setSaving(true);
        try {
            const payload: any = {
                name: data.name,
                locationType: data.locationType,
                incidentType: data.incidentType,
            };

            // Add client ID if super admin
            if (data.client) {
                payload.clientID = data.client.id;
            }

            // Add department ID if selected
            if (data.department) {
                payload.departmentID = data.department.id;
            }

            // Add location-specific fields
            if (data.locationType === "country" && data.countryCodes) {
                payload.countryISOs = data.countryCodes;
            } else if (data.locationType === "facility" && data.facilityIDs) {
                payload.facilityIDs = data.facilityIDs;
                payload.radius = data.radius;
            } else if (data.locationType === "custom" && data.customShapes) {
                // Convert custom shapes to simple array format for backend
                payload.customShapes = data.customShapes;
            }

            // Add tags-specific fields
            if (data.incidentType === "tags") {
                payload.incidentFeedIDs = data.incidentFeedIDs || [];
                payload.incidentTags = (data.incidentTags || []).map(tag => tag[0]); // slugs
                payload.incidentTagLabels = (data.incidentTags || []).map(tag => tag[1]); // labels
                payload.excludedTags = (data.excludedTags || []).map(tag => tag[0]); // slugs
                payload.excludedTagLabels = (data.excludedTags || []).map(tag => tag[1]); // labels
                payload.incidentTagMode = data.tagMode || "any";
            }

            // Add categories-specific fields
            if (data.incidentType === "categories") {
                payload.incidentTypes = data.categoryTypeIDs || [];
                payload.incidentSeverities = data.incidentSeverities || [];
            }

            // Add notification settings
            if (data.locationType === "facility") {
                payload.notifyFacilityGuardians = data.notifyFacilityGuardians || false;
            }
            payload.notifyTrackingTeam = data.notifyTrackingTeam || false;
            payload.alertRecipients = data.alertRecipients || [];
            payload.notificationGroupIDs = data.notificationGroupIDs || [];

            if (mode === "edit" && alertId) {
                await apiV2.patch(`site-based-alerts/${alertId}`, payload);
            } else {
                await apiV2.post("site-based-alerts", payload);
            }
            navigate(prevUrl);
        } catch (e) {
            if (isAxiosError(e) && e.response?.data?.error) {
                setError(e.response.data.error);
            } else {
                setError("Failed to save alert");
            }
        } finally {
            setSaving(false);
        }
    }, [data, navigate, alertId, mode]);

    return (
        <div className="flex flex-col gap-6">
            <h2 className="text-xl font-semibold">Review Site Alert</h2>

            <div className="flex flex-col gap-4">
                {/* Alert Name */}
                <div className="border border-light-light-grey rounded p-4">
                    <div className="font-medium text-grey text-sm mb-1">Alert Name</div>
                    <div>{data.name}</div>
                </div>

                {/* Client */}
                {data.client && (
                    <div className="border border-light-light-grey rounded p-4">
                        <div className="font-medium text-grey text-sm mb-1">Client</div>
                        <div>{data.client.name}</div>
                    </div>
                )}

                {/* Location */}
                <div className="border border-light-light-grey rounded p-4">
                    <div className="font-medium text-grey text-sm mb-1">Location Type</div>
                    <div className="capitalize">{data.locationType}</div>

                    {data.locationType === "country" && data.countryCodes && data.countryCodes.length > 0 && (
                        <>
                            <div className="font-medium text-grey text-sm mt-3 mb-1">
                                {data.countryCodes.length === 1 ? "Country" : "Countries"}
                            </div>
                            <div className="space-y-1">
                                {data.countryCodes.map((countryCode) => {
                                    const country = countries.find(c => c.countryCode === countryCode);
                                    return country ? (
                                        <div key={countryCode} className="text-sm flex items-center gap-2">
                                            <span>{country.flag}</span>
                                            <span>{country.countryNameEn}</span>
                                        </div>
                                    ) : null;
                                })}
                            </div>
                        </>
                    )}

                    {data.locationType === "facility" && data.facilities && data.facilities.length > 0 && (
                        <>
                            <div className="font-medium text-grey text-sm mt-3 mb-1">
                                {data.facilities.length === 1 ? "Facility" : "Facilities"}
                            </div>
                            <div className="space-y-1">
                                {data.facilities.map((facility) => (
                                    <div key={facility.id} className="text-sm">
                                        {facility.name}
                                        {facility.address?.formatted && (
                                            <span className="text-grey ml-2">
                                                - {facility.address.formatted}
                                            </span>
                                        )}
                                    </div>
                                ))}
                            </div>
                            <div className="font-medium text-grey text-sm mt-3 mb-1">Radius</div>
                            <div>{data.radius ? (data.radius / 1000).toLocaleString() : 0} km</div>
                        </>
                    )}

                    {data.locationType === "custom" && data.customShapes && (
                        <>
                            <div className="font-medium text-grey text-sm mt-3 mb-1">Custom Shapes</div>
                            <div>{data.customShapes.length} shape(s) defined</div>
                            <div className="mt-2 space-y-1">
                                {data.customShapes.map((shape, index) => (
                                    <div key={index} className="text-sm">
                                        {shape.type === "circle" ? (
                                            <span>Circle {index + 1}: {shape.radius ? (shape.radius / 1000).toFixed(2) : 0} km radius</span>
                                        ) : shape.type === "polygon" ? (
                                            <span>Polygon {index + 1}: {shape.geometry?.length || 0} points</span>
                                        ) : (
                                            <span>Polyline {index + 1}: {shape.geometry?.length || 0} points</span>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </>
                    )}
                </div>

                {/* Incident Type */}
                <div className="border border-light-light-grey rounded p-4">
                    <div className="font-medium text-grey text-sm mb-1">Incident Mode</div>
                    <div className="capitalize">{data.incidentType}</div>
                </div>

                {/* Tags (if tags mode) */}
                {data.incidentType === "tags" && (
                    <>
                        {/* Incident Feeds */}
                        {data.incidentFeedIDs && data.incidentFeedIDs.length > 0 && (
                            <div className="border border-light-light-grey rounded p-4">
                                <div className="font-medium text-grey text-sm mb-1">Incident Feeds</div>
                                <div>{data.incidentFeedIDs.length} feed(s) selected</div>
                            </div>
                        )}

                        {/* Included Tags */}
                        {data.incidentTags && data.incidentTags.length > 0 && (
                            <div className="border border-light-light-grey rounded p-4">
                                <div className="font-medium text-grey text-sm mb-1">
                                    Included Tags ({data.tagMode === "all" ? "Match All" : "Match Any"})
                                </div>
                                <div className="flex flex-wrap gap-2 mt-2">
                                    {data.incidentTags.map((tag) => (
                                        <span key={tag[0]} className="px-2 py-1 bg-light-blue rounded text-sm">
                                            {tag[1]}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Excluded Tags */}
                        {data.excludedTags && data.excludedTags.length > 0 && (
                            <div className="border border-light-light-grey rounded p-4">
                                <div className="font-medium text-grey text-sm mb-1">Excluded Tags</div>
                                <div className="flex flex-wrap gap-2 mt-2">
                                    {data.excludedTags.map((tag) => (
                                        <span key={tag[0]} className="px-2 py-1 bg-light-red rounded text-sm">
                                            {tag[1]}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        )}
                    </>
                )}

                {/* Categories (if categories mode) */}
                {data.incidentType === "categories" && (
                    <>
                        {data.categoryTypeIDs && data.categoryTypeIDs.length > 0 && (
                            <div className="border border-light-light-grey rounded p-4">
                                <div className="font-medium text-grey text-sm mb-1">Selected Incident Types</div>
                                <div>{data.categoryTypeIDs.length} type(s) selected</div>
                            </div>
                        )}

                        {data.incidentSeverities && data.incidentSeverities.length > 0 && (
                            <div className="border border-light-light-grey rounded p-4">
                                <div className="font-medium text-grey text-sm mb-1">Selected Severities</div>
                                <div className="flex flex-wrap gap-2 mt-2">
                                    {data.incidentSeverities.map((severity) => (
                                        <span key={severity} className="px-2 py-1 bg-light-blue rounded text-sm capitalize">
                                            {severity}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        )}
                    </>
                )}

                {/* Notification Settings */}
                <div className="border border-light-light-grey rounded p-4">
                    <div className="font-medium text-grey text-sm mb-1">Notifications</div>

                    {data.locationType === "facility" && (
                        <div className="mb-2">
                            <span className="font-medium">Notify Facility Guardians: </span>
                            <span>{data.notifyFacilityGuardians ? "Yes" : "No"}</span>
                        </div>
                    )}

                    <div className="mb-2">
                        <span className="font-medium">Notify Tracking Team: </span>
                        <span>{data.notifyTrackingTeam ? "Yes" : "No"}</span>
                    </div>

                    <div>
                        <span className="font-medium">Alert Recipients: </span>
                        <span>{data.alertRecipients && data.alertRecipients.length > 0
                            ? `${data.alertRecipients.length} user(s) selected`
                            : "None selected"}</span>
                    </div>
                    <div>
                        <span className="font-medium">Notification Groups: </span>
                        <span>{data.notificationGroupIDs && data.notificationGroupIDs.length > 0
                            ? `${data.notificationGroupIDs.length} group(s) selected`
                            : "None selected"}</span>
                    </div>
                </div>
            </div>

            <div className="flex justify-between gap-8">
                <ButtonV2
                    label="Back"
                    onClick={onBack}
                    color="gray"
                    disabled={saving}
                />
                <ButtonV2
                    label="Save Alert"
                    onClick={handleSave}
                    disabled={saving}
                />
            </div>

            {error && (
                <div className="flex items-center gap-2 px-4 py-3 rounded-lg bg-red/10 text-red text-sm">
                    {error}
                </div>
            )}
        </div>
    );
}

// Add more steps here as needed

type CreateSiteAlertPageProps = {
    alertId?: string;
    initialData?: Partial<AlertFormData>;
    mode?: "create" | "edit";
}

export function CreateSiteAlertPage({alertId, initialData, mode = "create", prevUrl: prevUrlProp}: CreateSiteAlertPageProps & {prevUrl?: string} = {}) {
    const {isSuperAdmin, isDepartmentAdmin} = useAuth();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const prevUrl = prevUrlProp ?? searchParams.get("prev") ?? "/site-alerts";
    const [currentStep, setCurrentStep] = useState(1);
    const [formData, setFormData] = useState<AlertFormData>({
        locationType: null,
        radius: undefined,
        incidentFeedIDs: [],
        incidentTags: [],
        excludedTags: [],
        tagMode: "any",
        categoryTypeIDs: [],
        incidentSeverities: [],
        ...initialData,
    });

    const handleUpdate = useCallback((updates: Partial<AlertFormData>) => {
        setFormData(prev => ({...prev, ...updates}));
    }, []);

    const handleNext = useCallback(() => {
        setCurrentStep(prev => prev + 1);
    }, []);

    const handleBack = useCallback(() => {
        setCurrentStep(prev => Math.max(1, prev - 1));
    }, []);

    const stepProps: StepProps = {
        data: formData,
        onUpdate: handleUpdate,
        onNext: handleNext,
        onBack: handleBack,
        isSuperAdmin,
        isDepartmentAdmin,
    };

    // Define your steps here - add more as needed
    // Steps: location type (with auto incident mode), location, [tags/categories selection], notification, review
    const totalSteps = 5;

    return (
        <div className="flex flex-col gap-6 w-full max-w-4xl mx-auto">
            <nav className="flex items-center gap-2 text-sm text-light-grey">
                <Link to="/sites" className="hover:text-grey">Sites</Link>
                <span>&gt;</span>
                <Link to={prevUrl} className="hover:text-grey">Site-Based Alerts</Link>
                <span>&gt;</span>
                <span className="text-grey">{mode === "edit" ? "Edit Site Alert" : "Create Site Alert"}</span>
            </nav>
            <div className="flex justify-between items-center">
                <h1 className="text-2xl font-semibold">{mode === "edit" ? "Edit Site Alert" : "Create Site Alert"}</h1>
                <div className="text-sm text-grey">
                    Step {currentStep} of {totalSteps}
                </div>
            </div>

            {/* Progress bar */}
            <div className="w-full bg-light-light-grey rounded-full h-2">
                <div
                    className="bg-blue h-2 rounded-full transition-all"
                    style={{width: `${(currentStep / totalSteps) * 100}%`}}
                />
            </div>

            {/* Step content */}
            <div className="bg-white p-6 rounded-lg shadow w-full overflow-visible">
                {currentStep === 1 && <LocationTypeStep {...stepProps} />}
                {currentStep === 2 && formData.locationType === "custom" && <CustomLocationStep {...stepProps} />}
                {currentStep === 2 && formData.locationType !== "custom" && <LocationSelectionStep {...stepProps} />}
                {currentStep === 3 && formData.incidentType === "tags" && <TagSelectionStep {...stepProps} />}
                {currentStep === 3 && formData.incidentType === "categories" && <CategorySelectionStep {...stepProps} />}
                {currentStep === 4 && <NotificationStep {...stepProps} />}
                {currentStep === 5 && <ReviewStep {...stepProps} navigate={navigate} alertId={alertId} mode={mode} prevUrl={prevUrl} />}
            </div>
        </div>
    );
}
